import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDebouncedPendingSave } from '@dealstack/frontend/shared/hooks/useDebouncedPendingSave';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { useValuationsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationsQuery';
import { upsertValuation } from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { ValuationDetailsForm } from '@dealstack/frontend/app/pages/Valuations/constants.types';
import { VALUATION_INPUT_DEBOUNCE_MS } from '@dealstack/shared/lib/debounceValues';
import {
	VALUATION_COMPUTE_START_QUERY_KEY,
	VALUATION_SAVE_MUTATION_KEY,
	VALUATIONS_QUERY_KEY,
} from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { ValuationWithClassifierRes } from '@dealstack/shared/lib/generated';
import { toastError } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

export function useValuationUpsert() {
	const { dealId, pathValuationId } = useValuationNavigation();
	const queryClient = useQueryClient();
	const {
		valuations,
		isLoading: isValuationsLoading,
		updateValuationCache,
	} = useValuationsQuery(dealId);
	const currentValuation = useMemo(
		() => valuations.find((v) => v.id === pathValuationId),
		[valuations, pathValuationId]
	);

	const { save, flush, cancel } = useDebouncedPendingSave<
		ValuationDetailsForm,
		ValuationWithClassifierRes
	>({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, pathValuationId ?? ''],
		debounceMs: VALUATION_INPUT_DEBOUNCE_MS,
		mutationFn: async (formValues) => {
			if (!pathValuationId || !dealId || !formValues.valuationDate) {
				return null;
			}

			const response = await upsertValuation({
				valuationId: pathValuationId,
				dealId,
				data: {
					name: formValues.valuationTitle,
					defaultCurrency: formValues.valuationDefaultCurrency,
					defaultNumberFormat: formValues.valuationMultiples,
					valuationDate: formValues.valuationDate,
					classifierIds:
						Array.isArray(formValues.valuationTags) &&
						formValues.valuationTags.length > 0
							? formValues.valuationTags
							: undefined,
				},
			});

			return response.payload ?? null;
		},
		onSuccess: (payload) => {
			if (!payload) {
				return;
			}
			// If the row is already in the list cache (any save after the first),
			// keep it in sync. The very first upsert creates the row, so the list
			// won't have it yet - in that case invalidate so the next fetch picks
			// it up.
			const cached = queryClient
				.getQueryData<ValuationWithClassifierRes[]>([VALUATIONS_QUERY_KEY, dealId])
				?.some((v) => v.id === payload.id);
			if (cached) {
				updateValuationCache(payload);
			} else {
				void queryClient.invalidateQueries({ queryKey: [VALUATIONS_QUERY_KEY, dealId] });
			}
			// Valuation settings changed → start a fresh compute (new workflowId picks up the change).
			void queryClient.invalidateQueries({
				queryKey: [VALUATION_COMPUTE_START_QUERY_KEY, dealId ?? '', payload.id],
			});
		},
		onError: (error) => toastError('Error saving valuation', error),
	});

	const upsertValuationApi = useMemo(() => ({ save, flush, cancel }), [save, flush, cancel]);

	return {
		currentValuation,
		isValuationsLoading,
		upsertValuation: upsertValuationApi,
	};
}
